import React, { createContext, useContext, useEffect, useState, PropsWithChildren, useCallback } from 'react';
import { getAuthUser, isAuthLoaded, onAuthReady } from '@/lib/authState';
import { auth } from '@/lib/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { getValidToken } from '@/js/external-js/variableModules/utils/getValidToken.util';
import { AUTH_CHANNEL_NAME } from '@/services/logoutService';

interface AuthContextValue {
  isAuthReady: boolean;
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  sessionId: string | null;
  forceLogout?: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

import { getOrCreateSessionId } from '@/js/external-js/variableModules/utils/getValidToken.util';
import { onAuthEvent, signOut, forceSignOut } from '@/services/logoutService';
import { signOut as firebaseSignOut } from 'firebase/auth';
import { useResetState } from '@/utils/stateReset';
import { clearMspViewSession } from '@/utils/mspViewSession';

export const AuthProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const { registerReset } = useResetState();
  const [isAuthReady, setIsAuthReady] = useState(isAuthLoaded());
  const [user, setUser] = useState<User | null>(getAuthUser());
  const [token, setToken] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const authChannel = typeof window !== 'undefined' ? new BroadcastChannel(AUTH_CHANNEL_NAME) : null;

  // Register reset function
  useEffect(() => {
    return registerReset(() => {
      console.log('[AuthContext] Resetting auth state');
      setUser(null);
      setToken(null);
      setSessionId(null);
      forceSignOut();
      setIsAuthReady(false);
      // Clear session tracking on logout
      clearMspViewSession();
    });
  }, [registerReset]);

  useEffect(() => {
    // Wait for initial auth state
    const unsubscribeReady = onAuthReady(() => {
      setIsAuthReady(true);
      setUser(getAuthUser());
    });

    // Listen for further auth changes
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setIsAuthReady(true);
      if (firebaseUser) {
        const sessionId = getOrCreateSessionId();
        setSessionId(sessionId);
        const t = await getValidToken(auth);
        setToken(t);
      } else {
        setToken(null);
        setSessionId(null);
      }
    });

    // Listen for auth events from other tabs
    const handleAuthEvent = (e: MessageEvent) => {
      const data = e.data;
      console.log('[AuthContext] Received auth event from another tab:', data?.type);
      
      if (data?.type === 'logout' && !data?.isLoginAttempt) {
        console.log('[AuthContext] Processing logout from another tab');
        setUser(null);
        setToken(null);
        setSessionId(null);
        forceSignOut();
        // Clear auth state if user is still logged in
        if (auth.currentUser) {
          firebaseSignOut(auth).catch(error => {
            console.error('[AuthContext] Error during cross-tab sign out:', error);
          });
        }
      } else if (data?.type === 'login' && data?.user) {
        console.log('[AuthContext] Processing login from another tab');
        handleLoginFromOtherTab(data.user);
      }
    };

    if (authChannel) {
      authChannel.addEventListener('message', handleAuthEvent);
    }

    // Also set sessionId on mount (in case user is already logged in)
    setSessionId(getOrCreateSessionId());

    return () => {
      unsubscribeReady();
      unsubscribeAuth();
      if (authChannel) {
        authChannel.removeEventListener('message', handleAuthEvent);
      }
    };
  }, []);

  // Function to handle login from another tab
  const handleLoginFromOtherTab = useCallback(async (user: User) => {
    console.log('[AuthContext] Handling login from another tab');
    setUser(user);
    const sessionId = getOrCreateSessionId();
    setSessionId(sessionId);
    const t = await getValidToken(auth);
    setToken(t);
  }, []);

  // Listen for login events from other tabs
  useEffect(() => {
    const handleLoginEvent = (data: any) => {
      if (data?.type === 'login' && data?.user) {
        console.log('[AuthContext] Received login event from another tab');
        handleLoginFromOtherTab(data.user);
      }
    };

    if (authChannel) {
      authChannel.addEventListener('message', handleLoginEvent);
      return () => {
        authChannel.removeEventListener('message', handleLoginEvent);
      };
    }
  }, [handleLoginFromOtherTab]);

  // --- Force Logout Implementation ---
  // Polls Firestore for forceLogout field on session doc
  useEffect(() => {
    if (!user || !sessionId) return;
    let unsub: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      const { doc, onSnapshot, getFirestore } = await import('firebase/firestore');
      const db = getFirestore();
      const sessionRef = doc(db, 'users', user.uid, 'sessions', sessionId);
      console.debug('[AuthContext] Polling session doc for forceLogout:', { sessionId, uid: user.uid, sessionRef });
      unsub = onSnapshot(sessionRef, (snap) => {
        if (cancelled) return;
        console.debug('[AuthContext] Session doc snapshot:', snap);
        if (snap.exists() && snap.data().forceLogout) {
          console.debug('[AuthContext] Force logout detected');
          forceLogout();
        } else {
          console.debug('[AuthContext] Session doc does not exist or no forceLogout field');
          // const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
          // if (!isLocalhost) {
          //   forceLogout();
          // }
        }
      });
    })();
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, [user, sessionId]);

  // The forceLogout method
  const forceLogout = useCallback(async () => {
    setUser(null);
    setToken(null);
    setSessionId(null);
    forceSignOut();
    if (auth.currentUser) {
      await firebaseSignOut(auth).catch(() => {});
    }
    // Optionally clear all storage for security
    try { localStorage.clear(); } catch {}
    try { sessionStorage.clear(); } catch {}
    // Redirect to /logout with forced param
    if (typeof window !== 'undefined') {
      window.location.href = '/logout?forced=1';
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthReady,
        isAuthenticated: !!user,
        user,
        token,
        sessionId,
        forceLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
